import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Receipt } from '../../../types';
import { createMapFromOptions, getPedidosTypeOptions } from '../RenderFunctions';
import { formatCurrency } from '../../../../../../utils/functions';
import ReceiptDetails from './components/ReceiptDetails/ReceiptDetails';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaBars, FaClock, FaChevronDown, FaChevronRight, FaLayerGroup } from 'react-icons/fa';
import { HiOutlineEye } from 'react-icons/hi2';
import {FaCashRegister} from 'react-icons/fa6';
import { RiEBike2Fill } from 'react-icons/ri';
import { MdOutlineTableBar } from 'react-icons/md';
import { format } from 'date-fns';
import {FaTriangleExclamation} from 'react-icons/fa6';

// Interface for grouped receipts
interface ReceiptGroup {
  correlationId: string;
  receipts: Receipt[];
  totalDue: number;
  isExpanded?: boolean;
  timestamp: number; // For sorting
}

// Interface for unified list item (either a group or a standalone receipt)
interface ListItem {
  type: 'group' | 'standalone';
  data: ReceiptGroup | Receipt;
  timestamp: number;
}

interface TableProps {
  data: Receipt[];
  setReceipts: (receipts: Receipt[]) => void;
}

const Table: React.FC<TableProps> = ({ data, setReceipts }) => {
  const [showSelection] = useState(false);
  const [selectedReceipts, setSelectedReceipts] = useState<Receipt[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState<Receipt | null>(null);
  const [pedidosTypeMap, setPedidosTypeMap] = useState<Map<number, string>>(new Map());
  const [isMobile, setIsMobile] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Helper to get timestamp from receipt
  const getReceiptTimestamp = (receipt: Receipt): number => {
    const ts = receipt.local_timestamp ?? receipt.timestamp;
    return ts ? new Date(ts).getTime() : 0;
  };

  // Group receipts by correlation_id and create unified sorted list
  const sortedItems = useMemo(() => {
    const groups = new Map<string, Receipt[]>();
    const standalone: Receipt[] = [];

    data.forEach(receipt => {
      const correlationId = receipt.receipt_metadata?.correlation_id;
      if (correlationId) {
        const existing = groups.get(correlationId) || [];
        existing.push(receipt);
        groups.set(correlationId, existing);
      } else {
        standalone.push(receipt);
      }
    });

    // Build unified list of items
    const items: ListItem[] = [];

    // Add groups (only those with more than 1 receipt)
    groups.forEach((receipts, correlationId) => {
      if (receipts.length > 1) {
        const totalDue = receipts.reduce((sum, r) => sum + r.total_due, 0);
        // Use the most recent timestamp from the group for sorting
        const groupTimestamp = Math.max(...receipts.map(r => getReceiptTimestamp(r)));
        items.push({
          type: 'group',
          data: { correlationId, receipts, totalDue, timestamp: groupTimestamp },
          timestamp: groupTimestamp
        });
      } else {
        // Single receipt in group, treat as standalone
        standalone.push(...receipts);
      }
    });

    // Add standalone receipts
    standalone.forEach(receipt => {
      items.push({
        type: 'standalone',
        data: receipt,
        timestamp: getReceiptTimestamp(receipt)
      });
    });

    // Sort by timestamp descending (most recent first)
    items.sort((a, b) => b.timestamp - a.timestamp);

    return items;
  }, [data]);

  const toggleGroupExpansion = (correlationId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(correlationId)) {
        newSet.delete(correlationId);
      } else {
        newSet.add(correlationId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    getPedidosTypeOptions().then((options) => {
      setPedidosTypeMap(createMapFromOptions(options));
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Cleanup effect para prevenir memory leaks
  useEffect(() => {
    return () => {
      setShowModal(false);
      setCurrentReceipt(null);
      setSelectedReceipts([]);
    };
  }, []);


  const handleSelectReceipt = (receipt: Receipt) => {
    setSelectedReceipts((prev) =>
      prev.some((r) => r.receipt_id === receipt.receipt_id)
        ? prev.filter((r) => r.receipt_id !== receipt.receipt_id)
        : [...prev, receipt]
    );
  };

  const handleOpenModal = useCallback((receipt: Receipt) => {
    // Forzar reset completo del estado del modal
    setShowModal(false);
    setCurrentReceipt(null);
    
    // Usar setTimeout para asegurar que el estado se actualice
    setTimeout(() => {
      setCurrentReceipt(receipt);
      setShowModal(true);
    }, 10);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    
    // Usar setTimeout para asegurar que el estado se limpia correctamente
    setTimeout(() => {
      setCurrentReceipt(null);
    }, 100);
  }, []);

  const isToday = (timestamp: string | number) => {
    const today = new Date();
    const date = new Date(timestamp);
    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    );
  };

  const formatDateOrTime = (timestamp: string | number) => {
    return isToday(timestamp)
      ? format(new Date(timestamp), 'HH:mm')
      : format(new Date(timestamp), 'dd/MM/yyyy');
  };

  // Get group status summary
  const getGroupStatusInfo = (receipts: Receipt[]) => {
    const allPaid = receipts.every(r => r.payed_full);
    const allCancelled = receipts.every(r => r.is_cancelled);
    const allRefunded = receipts.every(r => r.is_refunded);
    const anyPending = receipts.some(r => r.active && !r.payed_full && !r.is_cancelled && !r.is_refunded);
    
    if (allRefunded) return { className: 'reembolsed', text: 'Reembolsados' };
    if (allCancelled) return { className: 'cancelled', text: 'Cancelados' };
    if (allPaid) return { className: 'success', text: 'Pagados' };
    if (anyPending) return { className: 'warning', text: 'Pendientes' };
    return { className: 'mixed', text: 'Mixto' };
  };

  // Tailwind status badge styles — using mp-badge system
  const statusStyles: Record<string, string> = {
    reembolsed: 'mp-badge mp-badge-amber',
    cancelled: 'mp-badge mp-badge-danger',
    success: 'mp-badge mp-badge-success',
    warning: 'mp-badge mp-badge-warning',
    unactive: 'mp-badge mp-badge-neutral',
    mixed: 'mp-badge mp-badge-purple',
  };

  // Mobile card left-accent classes keyed by status
  const mobileAccentStyles: Record<string, string> = {
    success: 'mp-mobile-card-accent-success',
    warning: 'mp-mobile-card-accent-warning',
    cancelled: 'mp-mobile-card-accent-danger',
    reembolsed: 'mp-mobile-card-accent-amber',
    unactive: 'mp-mobile-card-accent-neutral',
    mixed: 'mp-mobile-card-accent-purple',
  };

  const getTypeIcon = (type: string | undefined, iconOnly: boolean = false) => {
    if (!type) return iconOnly ? <FaBars /> : "N/A";
    if (type === 'Caja') return iconOnly ? <FaCashRegister /> : <><FaCashRegister /> Caja</>;
    if (type === 'Delivery') return iconOnly ? <RiEBike2Fill /> : <><RiEBike2Fill /> Delivery</>;
    if (type === 'Restaurant') return iconOnly ? <MdOutlineTableBar /> : <><MdOutlineTableBar /> Restaurant</>;
    return iconOnly ? <FaBars /> : type;
  };

  const showReceiptStatus = (receipt: Receipt, iconOnly: boolean = false) => {
    if (receipt.is_refunded) {
      return (
        <span className={statusStyles.reembolsed}>
          {iconOnly ? <FaExclamationTriangle/> : (<><FaExclamationTriangle/> Reembolsado</>)}
        </span>
      );
    } else if (receipt.is_cancelled) {
      return (
        <span className={statusStyles.cancelled}>
          {iconOnly ? <FaTimesCircle /> : (<><FaTimesCircle /> Cancelado</>)}
        </span>
      );
    } else if (receipt.payed_full) {
      return (
        <span className={statusStyles.success}>
          {iconOnly ? <FaCheckCircle /> : (<><FaCheckCircle /> Pagado</>)}
        </span>
      );
    } else if (receipt.active) {
      return (
        <span className={statusStyles.warning}>
          {iconOnly ? <FaClock /> : (<><FaClock /> Pendiente</>)}
        </span>
      );
    } else {
      return (
        <span className={statusStyles.unactive}>
          {iconOnly ? '!' : (<><FaTriangleExclamation /> Pago Insuficiente</>)}
        </span>
      );
    }
  };

  // Helper function to get status class and text for mobile cards
  const getReceiptStatusInfo = (receipt: Receipt) => {
    if (receipt.is_refunded) {
      return { className: 'reembolsed', text: 'Reembolsado' };
    } else if (receipt.is_cancelled) {
      return { className: 'cancelled', text: 'Cancelado' };
    } else if (receipt.payed_full) {
      return { className: 'success', text: 'Pagado' };
    } else if (receipt.active) {
      return { className: 'warning', text: 'Pendiente' };
    } else {
      return { className: 'unactive', text: 'Inactivo' };
    }
  };

  // Render single mobile card
  const renderMobileCard = (receipt: Receipt, isSubReceipt: boolean = false) => {
    const statusInfo = getReceiptStatusInfo(receipt);
    const accentClass = mobileAccentStyles[statusInfo.className] || mobileAccentStyles.unactive;
    
    return (
      <div 
        key={receipt.receipt_id} 
        className={`mp-mobile-card ${accentClass} ${isSubReceipt ? 'ml-4' : ''}`}
        style={{ color: 'var(--text-color)' }}
      >
        <div className="cursor-pointer flex items-center gap-3" onClick={() => handleOpenModal(receipt)}>
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Name + Status */}
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-bold truncate">
                {isSubReceipt ? `#${receipt.receipt_number}` : (receipt.user_names ? receipt.user_names[0] : '—')}
              </span>
              <span className={statusStyles[statusInfo.className] || statusStyles.unactive}>
                {statusInfo.text}
              </span>
            </div>
            {/* Row 2: Meta badges + Amount */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 flex-wrap">
                {!isSubReceipt && (
                  <span className="inline-flex items-center gap-1 text-xs opacity-45">
                    {getTypeIcon(pedidosTypeMap.get(receipt.pedido_type_id!), true)}
                    {formatDateOrTime((receipt.local_timestamp ?? receipt.timestamp)!)}
                  </span>
                )}
                {receipt.mesa_num && !isSubReceipt && (
                  <span className="mp-badge mp-badge-info">
                    Mesa {receipt.mesa_num}
                  </span>
                )}
                {receipt.client_name && (
                  <span className="mp-badge mp-badge-neutral">
                    {receipt.client_name}
                  </span>
                )}
              </div>
              <span className="text-base font-extrabold tabular-nums" style={{ color: '#FC7556' }}>
                {formatCurrency(receipt.total_due)}
              </span>
            </div>
          </div>
          {/* Chevron hint */}
          <FaChevronRight className="text-xs opacity-20 flex-shrink-0" />
        </div>
      </div>
    );
  };

  // Render grouped mobile card
  const renderGroupedMobileCard = (group: ReceiptGroup) => {
    const firstReceipt = group.receipts[0];
    const statusInfo = getGroupStatusInfo(group.receipts);
    const accentClass = mobileAccentStyles[statusInfo.className] || mobileAccentStyles.unactive;
    const isExpanded = expandedGroups.has(group.correlationId);
    
    return (
      <div key={group.correlationId} className="space-y-2">
        <div className={`mp-mobile-card ${accentClass}`} style={{ color: 'var(--text-color)' }}>
          <div 
            className="cursor-pointer flex items-center gap-3"
            onClick={() => toggleGroupExpansion(group.correlationId)}
          >
            <div className="flex-1 min-w-0">
              {/* Row 1: Group label + Status */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 text-sm font-bold">
                  <span className="opacity-40 text-xs" style={{ transition: 'transform 0.2s ease' }}>
                    {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                  <FaLayerGroup className="text-orange-400 text-sm" />
                  <span>{group.receipts.length} Recibos</span>
                </div>
                <span className={statusStyles[statusInfo.className] || statusStyles.unactive}>
                  {statusInfo.text}
                </span>
              </div>
              {/* Row 2: Meta badges + Amount */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-xs opacity-45">
                    {getTypeIcon(pedidosTypeMap.get(firstReceipt.pedido_type_id!), true)}
                    {formatDateOrTime((firstReceipt.local_timestamp ?? firstReceipt.timestamp)!)}
                  </span>
                  {firstReceipt.mesa_num && (
                    <span className="mp-badge mp-badge-info">
                      Mesa {firstReceipt.mesa_num}
                    </span>
                  )}
                  <span className="mp-badge mp-badge-purple">
                    {firstReceipt.receipt_metadata?.split_type === 'equal' ? 'División igual' : 'División'}
                  </span>
                </div>
                <span className="text-base font-extrabold tabular-nums" style={{ color: '#FC7556' }}>
                  {formatCurrency(group.totalDue)}
                </span>
              </div>
            </div>
            {/* Chevron hint */}
            <FaChevronRight className={`text-xs opacity-20 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
        {isExpanded && (
          <div className="space-y-2 pl-3">
            {group.receipts.map(receipt => renderMobileCard(receipt, true))}
          </div>
        )}
      </div>
    );
  };

  // Render mobile card view
  const renderMobileView = () => (
    <div className="flex flex-col gap-2.5 px-0">
      {sortedItems.map(item => {
        if (item.type === 'group') {
          return renderGroupedMobileCard(item.data as ReceiptGroup);
        } else {
          return renderMobileCard(item.data as Receipt);
        }
      })}
      {/* Mobile footer count */}
      <div className="text-center py-3">
        <span className="text-xs opacity-40" style={{ color: 'var(--text-color)' }}>
          Mostrando <strong className="font-semibold">{data.length}</strong> recibos
        </span>
      </div>
    </div>
  );

  // Render single desktop table row
  const renderTableRow = (receipt: Receipt, isSubReceipt: boolean = false) => (
    <tr 
      key={receipt.receipt_id} 
      className={`cursor-pointer ${isSubReceipt ? 'bg-orange-50/5' : ''}`}
      onClick={() => handleOpenModal(receipt)}
    >
      {showSelection && (
        <td className="w-12" onClick={(e) => e.stopPropagation()}>
          <input
            id={`checkbox-${receipt.receipt_id}`}
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 accent-orange-500"
            checked={selectedReceipts.some((r) => r.receipt_id === receipt.receipt_id)}
            onChange={() => handleSelectReceipt(receipt)}
          />
        </td>
      )}
      <td className="font-medium hidden lg:table-cell">
        {isSubReceipt && <span className="inline-block w-4"></span>}
        {receipt.receipt_number}
      </td>
      <td className="hidden lg:table-cell">
        {receipt.client_name || <span className="opacity-25 italic">—</span>}
      </td>
      <td className="font-medium">
        {isSubReceipt ? <span className="opacity-25">—</span> : (receipt.user_names ? receipt.user_names[0] : <span className="opacity-25 italic">—</span>)}
      </td>
      <td className="hidden lg:table-cell">
        {isSubReceipt ? <span className="opacity-25">—</span> : (receipt.mesa_num || <span className="opacity-25 italic">—</span>)}
      </td>
      <td>
        {isSubReceipt ? <span className="opacity-25">—</span> : (
          <span className="inline-flex items-center gap-1.5">
            {getTypeIcon(pedidosTypeMap.get(receipt.pedido_type_id!), isMobile)}
          </span>
        )}
      </td>
      <td className="opacity-50 hidden lg:table-cell">
        {isSubReceipt ? <span className="opacity-25">—</span> : (receipt.shift_name || '—')}
      </td>
      <td>{showReceiptStatus(receipt, isMobile)}</td>
      <td className="opacity-50 hidden lg:table-cell">
        {isSubReceipt ? <span className="opacity-25">—</span> : formatDateOrTime((receipt.local_timestamp ?? receipt.timestamp)!)}
      </td>
      <td style={{ textAlign: 'right' }}>
        <span className="font-bold tabular-nums" style={{ color: '#FC7556' }}>
          {formatCurrency(receipt.total_due)}
        </span>
      </td>
      <td className="w-14 text-center">
        <button
          className="mp-btn-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenModal(receipt);
          }}
          onMouseDown={(e) => e.preventDefault()}
          type="button"
          title="Ver detalle"
        >
          <HiOutlineEye size={16} />
        </button>
      </td>
    </tr>
  );

  // Render grouped desktop table row (parent row)
  const renderGroupedTableRow = (group: ReceiptGroup) => {
    const firstReceipt = group.receipts[0];
    const statusInfo = getGroupStatusInfo(group.receipts);
    const isExpanded = expandedGroups.has(group.correlationId);
    
    return (
      <React.Fragment key={group.correlationId}>
        <tr 
          className="cursor-pointer"
          style={{ borderLeft: '3px solid #FC7556' }}
          onClick={() => toggleGroupExpansion(group.correlationId)}
        >
           {showSelection && <td className="w-12"></td>}
          <td className="hidden lg:table-cell">
            <div className="inline-flex items-center gap-2">
              <span className="opacity-35 text-xs">
                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
              </span>
              <FaLayerGroup style={{ color: '#FC7556' }} className="text-sm" />
              <span className="font-semibold">{group.receipts.length}</span>
            </div>
          </td>
          <td className="hidden lg:table-cell">
            <span className="mp-badge mp-badge-purple">
              {firstReceipt.receipt_metadata?.split_type === 'equal' ? 'División igual' : 'División'}
            </span>
          </td>
          <td className="font-medium">
            {firstReceipt.user_names ? firstReceipt.user_names[0] : <span className="opacity-25 italic">—</span>}
          </td>
          <td className="hidden lg:table-cell">
            {firstReceipt.mesa_num || <span className="opacity-25 italic">—</span>}
          </td>
          <td>
            <span className="inline-flex items-center gap-1.5">
              {getTypeIcon(pedidosTypeMap.get(firstReceipt.pedido_type_id!), isMobile)}
            </span>
          </td>
          <td className="opacity-50 hidden lg:table-cell">
            {firstReceipt.shift_name || '—'}
          </td>
          <td>
            <span className={statusStyles[statusInfo.className] || statusStyles.unactive}>
              {statusInfo.text}
            </span>
          </td>
          <td className="opacity-50 hidden lg:table-cell">
            {formatDateOrTime((firstReceipt.local_timestamp ?? firstReceipt.timestamp)!)}
          </td>
          <td style={{ textAlign: 'right' }}>
            <span className="font-bold tabular-nums" style={{ color: '#FC7556' }}>
              {formatCurrency(group.totalDue)}
            </span>
          </td>
          <td className="w-14 text-center">
            <span className="text-xs font-medium cursor-pointer" style={{ color: '#FC7556', opacity: 0.6 }}>
              {isExpanded ? 'Contraer' : 'Expandir'}
            </span>
          </td>
        </tr>
        {isExpanded && group.receipts.map(receipt => renderTableRow(receipt, true))}
      </React.Fragment>
    );
  };

  // Render desktop table view
  const renderDesktopView = () => (
    <div className="w-full">
      <div className="mp-table-card">
        <div className="overflow-x-auto overflow-y-auto mp-scrollbar" style={{ maxHeight: '70vh' }}>
          <table className="mp-table">
            <thead className="mp-table-head">
              <tr>
                {showSelection && <th className="w-12"></th>}
                <th className="hidden lg:table-cell">#</th>
                <th className="hidden lg:table-cell">Cliente</th>
                <th>Usuario</th>
                <th className="hidden lg:table-cell">Mesa</th>
                <th>Tipo</th>
                <th className="hidden lg:table-cell">Turno</th>
                <th>Estado</th>
                <th className="hidden lg:table-cell">Última actualización</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th className="w-14"></th>
              </tr>
            </thead>
            <tbody className="mp-table-body">
              {sortedItems.map(item => {
                if (item.type === 'group') {
                  return renderGroupedTableRow(item.data as ReceiptGroup);
                } else {
                  return renderTableRow(item.data as Receipt);
                }
              })}
            </tbody>
          </table>
        </div>
        <div className="mp-table-footer">
          <span className="text-sm opacity-50">
            Mostrando <strong className="font-semibold" style={{ opacity: 1 }}>{data.length}</strong> recibos
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center mp-table-card">
          <div className="text-sm opacity-40" style={{ color: 'var(--text-color)' }}>
            No hay recibos disponibles
          </div>
        </div>
      ) : (
        <>
          {isMobile ? renderMobileView() : renderDesktopView()}
        </>
      )}

      <ReceiptDetails
        key={currentReceipt?.receipt_id || 'no-receipt'}
        setReceipts={setReceipts}
        currentReceipt={currentReceipt}
        show={showModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Table;
